import '@/shared/features/quiz-folder/consts/events.quiz-folder';

import { trackEvent } from '@zep/logger/tracking';

import type { FolderCopyPlace } from '@/shared/features/quiz-folder/consts/events.quiz-folder';
import {
  reportFolderError,
  toTrackingFailReason,
} from '@/shared/features/quiz-folder/utils/folderTracking';
import type { QuizCopyContext } from '@/shared/tracking/quizCopySource';

import { type QuizCopyResult, trackQuizCopyComplete } from './trackCopyFunnel';

/** `copyQuiz` 가 받는 `location`(= referrer) 값 가운데 저장 흐름의 자리로 아는 것. */
export type CopyLocation =
  | 'gnb_copy'
  | 'play_jelly_copy_guide'
  | 'trial_question_gate'
  | 'guest_share_copy';

/** `location` → 저장 흐름의 `place`. 키가 유니온이라 진입점이 늘면 컴파일러가 빠진 매핑을 잡는다. */
const COPY_PLACE_BY_LOCATION: Record<CopyLocation, FolderCopyPlace> = {
  gnb_copy: 'play_gnb',
  play_jelly_copy_guide: 'play_jelly',
  trial_question_gate: 'play_trial_gate',
  guest_share_copy: 'share_modal',
};

const isCopyLocation = (location: string): location is CopyLocation =>
  location in COPY_PLACE_BY_LOCATION;

/** 플레이 브리지가 넘기는 자유 문자열도 받는다 — 목록 밖 값은 `copy_other` 로 모은다. */
export const toCopyPlace = (location: string): FolderCopyPlace =>
  isCopyLocation(location) ? COPY_PLACE_BY_LOCATION[location] : 'copy_other';

/**
 * 저장 위치 모달을 거치지 않는 복사(플레이·공유 모달)를 저장 흐름 이벤트에 잇는다.
 * 소유 여부는 이 경로에서 알 수 없어 싣지 않는다.
 */
export const trackCopySaveEntry = (
  place: FolderCopyPlace,
  sourceQuizsetId: string,
  { authRequired }: { authRequired: boolean },
) => {
  trackEvent('quiz_folder_save_entry_click', {
    place,
    intent: 'copy',
    quiz_set_id: sourceQuizsetId,
    auth_required: authRequired,
  });
};

/** 위치를 고르지 않는 복사라 항상 기본 내 퀴즈에 저장된다. */
const trackCopySaveResult = (
  place: FolderCopyPlace,
  sourceQuizsetId: string,
  result: QuizCopyResult,
) => {
  trackEvent('quiz_folder_save_location_save', {
    place,
    intent: 'copy',
    quiz_set_id: sourceQuizsetId,
    location_type: 'default',
    publish_confirmed: false,
    ...(result.status === 'success'
      ? { result: 'success', copied_quiz_set_id: result.copiedQuizsetId }
      : { result: 'fail', reason: toTrackingFailReason(result.error) }),
  });
  if (result.status === 'fail') {
    reportFolderError(result.error, {
      action: 'copy_without_location',
      quizSetId: sourceQuizsetId,
    });
  }
};

/**
 * 복사 결과 한 번으로 옛 퍼널(`*_copy_complete`)과 저장 흐름(`save_location_save`)을 함께 찍는다.
 * 복사가 일어나는 두 자리(제자리 복사, 착지 경로)가 같은 한 줄을 쓴다.
 */
export const trackCopyOutcome = (
  context: QuizCopyContext,
  sourceQuizsetId: string,
  result: QuizCopyResult,
) => {
  trackQuizCopyComplete(context, sourceQuizsetId, result);
  // 자리 표식 없이 들어온 경우(링크 직접 진입 등)는 따로 모은다.
  trackCopySaveResult(
    context.copyPlace ?? 'copy_other',
    sourceQuizsetId,
    result,
  );
};
